import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface NotificationConfigurationResponse {
  configuration: {
    id: string;
    type: string;
    configuration: EmailNotificationsConfiguration;
    subType: string | null;
    name: string | null;
    createdAt: string;
    updatedAt: string;
  };
  success: boolean;
}

/** IANA timezone IDs supported for digest timestamps. New tenants default to UTC. */
export const DIGEST_TIMEZONE_UTC = 'UTC' as const;
export const VALID_DIGEST_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
] as const;
export type DigestTimezoneId = (typeof VALID_DIGEST_TIMEZONES)[number];

export type EmailNotificationsConfiguration = {
  ENCOMPASS_ENABLED: boolean;
  INAPP_ENABLED: boolean;
  RECIPIENT_MODE: 'STATIC' | 'LOAN_ASSOCIATES' | 'BOTH';
  STATIC_RECIPIENTS: string[];
  FALLBACK_RECIPIENT: string;
  BATCH_ENABLED: boolean;
  BATCH_INTERVAL_MINUTES: number;
  /** IANA timezone for digest email timestamps. Omitted when not set; default UTC. */
  DIGEST_TIMEZONE?: string;
};

const _mockData: EmailNotificationsConfiguration = {
  RECIPIENT_MODE: 'STATIC',
  STATIC_RECIPIENTS: ['dlichter@true.ai'],
  FALLBACK_RECIPIENT: 'dlichter@true.ai',
  ENCOMPASS_ENABLED: true,
  INAPP_ENABLED: true,
  BATCH_ENABLED: false,
  BATCH_INTERVAL_MINUTES: 30,
  DIGEST_TIMEZONE: DIGEST_TIMEZONE_UTC,
};

const getNotificationConfiguration: QueryFunction<
  EmailNotificationsConfiguration,
  ['/admin/notification']
> = async () => {
  if (isMockServices()) return await mock(_mockData);

  //return _mockData; // Uncomment this line to use mock data

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<NotificationConfigurationResponse>(
    '/configurations/EmailNotifications',
    {
      method: 'GET',
    },
  );

  if (response.data?.configuration?.configuration) {
    return response.data.configuration.configuration;
  }

  // Return empty config if not found (new tenants default to UTC for digest timezone)
  return {
    RECIPIENT_MODE: 'STATIC',
    STATIC_RECIPIENTS: [],
    FALLBACK_RECIPIENT: '',
    ENCOMPASS_ENABLED: false,
    INAPP_ENABLED: false,
    BATCH_ENABLED: false,
    BATCH_INTERVAL_MINUTES: 30,
    DIGEST_TIMEZONE: DIGEST_TIMEZONE_UTC,
  } as EmailNotificationsConfiguration;
};

export default getNotificationConfiguration;
